import type { EntityData } from '@adornis/baseql/entities/types.js';
import type { BaseQLMiddleware } from '@adornis/baseql/metadata/types.js';
import { getRawCollection } from '@adornis/baseql/server/collections.js';
import { context } from '@adornis/baseql/server/context.js';
import { getMyself } from '@adornis/users/db/api/currentUserInfo.operations.js';
import { ExpectedError } from '@adornis/validation/errors/ExpectedError.js';
import { Person } from '../../users/entities/person.js';
import type { UserOnUserPayload } from '../../users/UserPolicy.js';
import { MARTINUS_ABAC_PAYLOAD } from '../abac-payloads/MartinusAbacPayload.js';
import { MartinusAccessController } from '../api/MartinusAccessController.js';

/**
 * Evaluates an ABAC action for a baseql operation.
 *
 * This is the general form of {@link checkAbacOnViewedUserMiddleware}: the "viewed person" is
 * optional, so the same middleware can guard operations that act on nobody in particular (a list, an
 * export, a config read) as well as operations that act on a specific person.
 *
 * The access controller denies when no policy matches, so an action without a policy is closed
 * rather than open.
 *
 * @param action the action to evaluate, e.g. `MartinusTemporaryActions.AUTHENTICATED`
 * @param opts.viewedUserIDArgument name of the operation parameter holding the viewed person's ID
 * @throws if there is no logged in user, or the action is not allowed
 */
export const abacOperationMiddleware =
  (action: string, opts?: { viewedUserIDArgument?: string }): BaseQLMiddleware =>
  async options => {
    // server-internal calls (jobs, workers, import pipelines) already run with full trust
    if (context.serverContext) return options.next();

    const currentUser = await getMyself<Person>()({
      _id: 1,
      roles: { name: 1, contexts: 1, permissions: 1 },
      sorgeberechtigteKinderIDs: 1,
      spendenKinderIDs: 1,
    });
    if (!currentUser) throw new ExpectedError('Nicht angemeldet.');

    let viewedUser: EntityData<Person> | null = null;
    if (opts?.viewedUserIDArgument) {
      /**
       * Read the viewed person raw, as the permission evaluation must not depend on what the current
       * user is allowed to see.
       */
      const viewedUserID = options.args[opts.viewedUserIDArgument] as string | undefined;
      if (viewedUserID) {
        const userCol = await getRawCollection<EntityData<Person>>(Person._collectionName);
        viewedUser = await userCol.findOne(
          { _id: viewedUserID },
          { projection: { _id: 1, roles: 1, sorgeberechtigteKinderIDs: 1, spendenKinderIDs: 1 } },
        );
      }
    }

    const answer = await MartinusAccessController.evaluate({
      type: MARTINUS_ABAC_PAYLOAD,
      action,
      currentUser,
      viewedUser,
    } as UserOnUserPayload);

    if (!answer.allowed) throw new ExpectedError(answer.reason);
    return options.next();
  };
